import { Button, DropdownSelect, Modal } from '@components/common';
import { DatePicker } from '@components/common/DatePicker';
import { FormInput } from '@components/common/FormField';
import React, { useState } from 'react';
import type {
  InventorySupplierInvoiceRecord,
  InventorySupplierRecord,
  RecordSupplierPaymentRequest,
} from '@/renderer/types/inventory';

interface PaymentMethodOption {
  id: number;
  name: string;
}

interface SupplierPaymentModalProps {
  invoice: InventorySupplierInvoiceRecord;
  supplier: InventorySupplierRecord | undefined;
  paymentMethods: PaymentMethodOption[];
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (
    request: Omit<RecordSupplierPaymentRequest, 'companyId' | 'outletId'>,
  ) => void;
}

export function SupplierPaymentModal({
  invoice,
  supplier,
  paymentMethods,
  isSaving,
  onClose,
  onSubmit,
}: SupplierPaymentModalProps) {
  const outstanding = invoice.total_amount - invoice.amount_paid;
  const today = new Date().toISOString().split('T')[0];

  const [paymentDate, setPaymentDate] = useState(today);
  const [amount, setAmount] = useState(String(outstanding.toFixed(2)));
  const [paymentMethodId, setPaymentMethodId] = useState(
    paymentMethods[0]?.id ? String(paymentMethods[0].id) : '',
  );
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const parsedAmount = parseFloat(amount) || 0;
  const isValid = parsedAmount > 0 && paymentDate;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    onSubmit({
      supplierId: invoice.supplier_id,
      paymentDate,
      amount: parsedAmount,
      paymentMethodId: paymentMethodId ? parseInt(paymentMethodId, 10) : null,
      reference: reference.trim() || null,
      notes: notes.trim() || null,
      allocations: [
        {
          invoiceId: invoice.id,
          amountApplied: Math.min(parsedAmount, outstanding),
        },
      ],
    });
  };

  const pmOptions = [
    { value: '', label: 'No payment method' },
    ...paymentMethods.map((pm) => ({ value: String(pm.id), label: pm.name })),
  ];

  return (
    <Modal
      isOpen
      onClose={isSaving ? () => {} : onClose}
      title="Record supplier payment"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Invoice summary */}
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Supplier</span>
            <span className="font-medium text-gray-900">
              {supplier?.name ?? 'Unknown'}
            </span>
          </div>
          <div className="mt-1 flex justify-between text-gray-600">
            <span>Invoice</span>
            <span>{invoice.invoice_number || '(no number)'}</span>
          </div>
          <div className="mt-1 flex justify-between text-gray-600">
            <span>Invoice total</span>
            <span>
              {invoice.total_amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="mt-1 flex justify-between text-gray-600">
            <span>Already paid</span>
            <span>
              {invoice.amount_paid.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 font-semibold text-gray-900">
            <span>Outstanding</span>
            <span>
              {outstanding.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <DatePicker
            mode="single"
            label="Payment date *"
            value={paymentDate}
            onChange={setPaymentDate}
          />
          <FormInput
            label="Amount *"
            type="number"
            min="0.01"
            step="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={isSaving}
          />
        </div>

        <DropdownSelect
          label="Payment method"
          value={paymentMethodId}
          options={pmOptions}
          onChange={(v) => setPaymentMethodId(String(v))}
          disabled={isSaving}
        />

        <FormInput
          label="Reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Cheque number, transfer ref, etc."
          disabled={isSaving}
        />

        <FormInput
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional internal note"
          disabled={isSaving}
        />

        {parsedAmount > outstanding && (
          <p className="rounded-lg bg-yellow-50 p-2 text-xs text-yellow-700">
            Amount exceeds outstanding balance. The overpayment will be applied
            to this invoice but you may want to adjust the amount.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving} disabled={!isValid}>
            Record payment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
